import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { CartItemService } from "../../services/cart-item/cart-item.service";
import { CartAttributes, RemoveCartItem } from "@models/cart.model";
import { CartDetails, ProductAttributes, ProductCategories } from "@models/product.model";
import { LocalStorageService } from "../../services/local-storage/local-storage.service";
import { Router } from "@angular/router";
import { GenericDialogComponent } from "../../shared/generic-dialog/generic-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { appConfig } from "../../../environments/app.config";
import { TranslateService } from "@ngx-translate/core";
import { debounceTime, Subject, Subscription } from "rxjs";
import { AuthService } from "../../services/auth/auth.service";
import { DialogService } from "../../services/dialog/dialog.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { ColorsPicker } from "../../../utils/color-constants";

@Component({
    selector: "app-right-navbar",
    templateUrl: "./right-navbar.component.html",
    styleUrls: ["./right-navbar.component.scss"],
    standalone: false,
})
export class RightNavbarComponent implements OnInit, OnDestroy {
    @Output() closeEvent = new EventEmitter<void>();

    public cartItems: CartAttributes[] = [];
    public products: CartDetails[] = [];
    public cartId!: number;
    public totalMRP!: number;
    public totalDiscount!: number;
    public totalAmount!: number;
    public minQuantity = appConfig.minProductQuantity;
    public maxQuantity = appConfig.maxProductQuantity;
    public isCheckoutEnabled: boolean = false;
    public minOrderValue = appConfig.minOrderValue;
    public categories = ProductCategories;

    private cartUpdateSubject = new Subject<{ productId: number; quantity: number; cartUUID: string }>();

    private subscriptions = new Subscription();

    constructor(
        private dialog: MatDialog,
        private cartService: CartItemService,
        private localStorageService: LocalStorageService,
        private router: Router,
        private translate: TranslateService,
        private authService: AuthService,
        private dialogService: DialogService,
        private snackBar: MatSnackBar,
        private cartItemService: CartItemService,
    ) {}

    ngOnInit(): void {
        const navbarCartItemsSubscription = this.cartService.cartItems$.subscribe((cartItems) => {
            this.cartItems = cartItems;
            this.products = cartItems.map((item: CartAttributes) => ({
                product: item.product!,
                singleBlockInfo: item.singleBlockInfo!,
                cartUUID: item.cartUUID,
            }));

            if (this.products.length === 0) {
                this.closeNavbar();
                return;
            }
            this.calculateTotalValues();
        });

        this.subscriptions.add(navbarCartItemsSubscription);

        this.cartUpdateSubject
            .pipe(
                debounceTime(250), // Adjust the time as needed
            )
            .subscribe(({ productId, quantity, cartUUID }) => {
                this.updateCartQuantity(productId, quantity, cartUUID);
            });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    public closeNavbar(): void {
        this.closeEvent.emit();
    }

    public onDeleteCartItem(item: CartDetails): void {
        this.openDialog(item);
    }

    private openDialog(item: CartDetails): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.RemoveFromCart,
                title: this.getTranslation("genericDialogBox.removeCart.title"),
                image: item.product?.images?.imagesUrl[0],
                content: this.getTranslation("genericDialogBox.removeCart.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.removeBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) this.removeProductFromCart(item.product?.id!, item.cartUUID!);
        });
    }

    private calculateTotalValues(): void {
        this.cartService.calculateTotalMRP(this.cartItems);
        this.totalMRP = this.cartService.totalMRP;
        this.totalDiscount = this.cartService.totalDiscount;
        this.totalAmount = this.cartService.subTotal;
        this.isCheckoutEnabled = this.totalAmount >= appConfig.minOrderValue;
    }

    public getProductQtyInCart(productId: number, cartUUID: string): number {
        return this.localStorageService.getCartedQuantity(productId, cartUUID);
    }

    private updateCartQuantity(productId: number, quantity: number, cartUUID: string): void {
        if (quantity < 1) {
            this.removeProductFromCart(productId, cartUUID);
        } else if (quantity <= 99) {
            this.updateProductQuantityInCart(productId, quantity, cartUUID);
        }
    }

    private removeProductFromCart(id: number, cartUUID: string): void {
        const item: RemoveCartItem = { productId: id };
        if (cartUUID) {
            item.cartUUID = cartUUID;
        }
        this.cartService.removeCartItem([item]).subscribe((response) => {
            if (response) {
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
            }
        });
    }

    private updateProductQuantityInCart(productId: number, quantity: number, cartUUID: string): void {
        this.cartService.updateCartQuantity(productId, quantity, cartUUID).subscribe();
    }

    public onClick(value: number, productId: number, cartUUID: string): void {
        const quantityInCart = this.getProductQtyInCart(productId, cartUUID);
        const quantity = quantityInCart + value;
        this.cartUpdateSubject.next({ productId, quantity, cartUUID });
    }

    public onBlur(event: Event, productId: number, cartUUID: string): void {
        const inputValue = (event.target as HTMLInputElement).value.trim();
        if (!inputValue) {
            (event.target as HTMLInputElement).value = "1";
            const quantity = parseInt((event.target as HTMLInputElement).value, 10);
            this.cartUpdateSubject.next({ productId, quantity, cartUUID });
        }
    }

    public onInputChange(event: Event, productId: number, cartUUID: string): void {
        let inputValue = (event.target as HTMLInputElement).value.trim();
        let quantity = parseInt(inputValue, 10);

        if (isNaN(quantity)) {
            return;
        } else if (quantity < 1) {
            (event.target as HTMLInputElement).value = "1";
            quantity = parseInt((event.target as HTMLInputElement).value, 10);
        } else if (inputValue.length > 2) {
            inputValue = inputValue.slice(0, 2);
            (event.target as HTMLInputElement).value = inputValue;
            return;
        }

        this.cartUpdateSubject.next({ productId, quantity, cartUUID });
    }

    public onClickProduct(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public onCheckout(): void {
        if (!this.authService.isUserLoggedIn) {
            this.loginDialog();
        } else {
            this.router.navigate(["checkout/address"]);
        }
    }

    public loginDialog(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.LoginDialog,
        };
        this.dialogService
            .openDialog(dialogData, "90%", { disableClose: false }, "450px")
            .afterClosed()
            .subscribe(() => {
                this.router.navigate(["checkout/address"]);
            });
    }

    public extractFacesByType(data: CartDetails, type: string): string[] {
        return this.cartService.getFacesByType(data, type);
    }

    public getFaceColors(colorObject: object | undefined): { key: string; value: string }[] {
        if (!colorObject) {
            return [];
        }
        return Object.entries(colorObject).map(([key, value]) => ({
            key,
            value: value as string, // Ensure the value is treated as a string
        }));
    }

    public getdiscountPercentage(product: ProductAttributes): number {
        return Math.round(((product.price - product?.discountedPrice!) / product.price) * 100);
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public getColorValue(color: string): string | undefined {
        return ColorsPicker.find((colorItem) => colorItem.name === color)?.value;
    }

    public getFacesByType(data: CartDetails, type: string): string[] {
        return this.cartItemService.getFacesByType(data, type);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
